import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, ArrowRight, Globe, Inbox, } from "lucide-react";
import { api } from "../api";
import { AiNotiz } from "../components/AiNotiz";
import {
  Badge, Card, CardGrid, Glyph, Page, PageHeader, Skeleton, Ton,
} from "../ui";

type PublicFile = {
  id: string;
  name: string;
  size: number;
  folder: string;
  created_at: string;
};

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
}

/** Eine Kachel der Startseite. Titel, Symbol, eine Zahl — mehr braucht der
 *  Einstieg nicht, und mehr steht hier deshalb auch nicht. */
function Einstieg({
  to, titel, icon, ton, wert,
}: {
  to?: string; titel: string; icon: React.ReactNode; ton: Ton; wert: React.ReactNode;
}) {
  const inhalt = (
    <>
      <div className="ui-card-top">
        <Glyph ton={ton} tint>{icon}</Glyph>
        <div className="ui-card-title">{titel}</div>
        {to && <ArrowRight className="icon-svg public-card-arrow" />}
      </div>
      <div className="public-card-stat">{wert}</div>
    </>
  );
  return to
    ? <Card as={Link} to={to} klickbar>{inhalt}</Card>
    : <Card>{inhalt}</Card>;
}

/** Was ein nicht angemeldeter Besucher sieht: der Container, der Zustand des
 *  Servers, die freigegebenen Notizen und der Hinweis für Agenten. Was eine
 *  Anmeldung braucht, steht in der Seitenleiste — aufgezählt werden muss es
 *  hier nicht. */
export function PublicHome() {
  const [files, setFiles] = useState<PublicFile[] | null>(null);
  const [online, setOnline] = useState<boolean | null>(null);
  /** Wie viele Seiten öffentlich gelistet sind. */
  const [seiten, setSeiten] = useState<number | null>(null);

  useEffect(() => {
    let leben = true;
    api<{ files: PublicFile[] }>("/container/list")
      .then((r) => { if (leben) setFiles(r.files ?? []); })
      .catch(() => { if (leben) setFiles([]); });
    api<{ status: string }>("/health")
      .then((r) => { if (leben) setOnline(r.status === "ok"); })
      .catch(() => { if (leben) setOnline(false); });
    api<{ sites: unknown[] }>("/public/sites")
      .then((r) => { if (leben) setSeiten((r.sites ?? []).length); })
      .catch(() => { if (leben) setSeiten(0); });
    return () => { leben = false; };
  }, []);

  const gesamt = files?.reduce((n, f) => n + f.size, 0) ?? 0;
  const laedt = <Skeleton h={18} w={90} />;

  return (
    <Page className="public-home">
      <PageHeader titel="home" />

      <AiNotiz
        titel="Public entry page of a private home server"
        beschreibung={
          // Was hier steht, liest jeder — auch ohne Anmeldung. Also steht
          // hier nur, was ohnehin offen ist. Die Aufzählung dessen, was es
          // sonst noch gibt, stand einmal hier und war genau das, was auf
          // einer öffentlichen Seite nichts zu suchen hat: eine Liste der
          // Türen, an denen zu rütteln sich lohnt.
          "Lists what this server shares without a sign-in: public files, "
          + "public pages, and public storage modules. Anything not listed "
          + "here needs an account."
        }
        endpunkte={[
          { pfad: "/container/list", zweck: "the public files with name, size and date" },
          { pfad: "/public/files/modules", zweck: "the storage modules marked public" },
          { pfad: "/public/sites", zweck: "the pages listed publicly" },
          { pfad: "/health", zweck: "whether the server is up" },
        ]}
        daten={{
          numberOfItems: files?.length ?? 0,
          contentSize: gesamt,
        }}
      />

      <CardGrid>
        <Einstieg
          to="/storage"
          titel="Public files"
          icon={<Inbox />}
          ton="info-3"
          wert={files === null ? laedt : (
            <>
              <strong>{files.length}</strong> {files.length === 1 ? "file" : "files"}
              {" · "}{formatSize(gesamt)}
            </>
          )}
        />
        <Einstieg
          titel="Server"
          icon={<Activity />}
          ton={online === false ? "danger" : "success"}
          wert={online === null ? laedt : (
            <Badge ton={online ? "success" : "danger"}>
              {online ? "reachable" : "unreachable"}
            </Badge>
          )}
        />
        <Einstieg
          to="/sites"
          titel="Pages"
          icon={<Globe />}
          ton="accent"
          wert={seiten === null ? laedt : (
            <><strong>{seiten}</strong> {seiten === 1 ? "page" : "pages"}</>
          )}
        />
      </CardGrid>

    </Page>
  );
}
